export type ErrorRecord<E> = Record<keyof E, string>;
export interface ValidationResult {
    valid: boolean;
    error?: string | ErrorRecord<any>;
    exitEarly?: boolean;
    nameOfPipe?: string;
}

export interface PipeOptions {
    stopOnError?: boolean;
    nameOfPipe?: string;
}

export interface ExecutionEntry {
    type: 'transform' | 'validate';
    index: number;
    valid: boolean;
    result?: any;
    validation?: ValidationResult;
    error?: string | ErrorRecord<any>;
    exitEarly?: boolean;
    nameOfPipe?: string;
}

export interface ErrorEntry {
    index: number;
    error: string | ErrorRecord<any>;
    nameOfPipe?: string;
}

function isPromiseLike<T>(v: any): v is PromiseLike<T> {
    return v !== null && typeof v?.then === 'function';
}

function getErrorMessage(error: unknown): string | ErrorRecord<any> {
    if (error instanceof Error) {
        return error.message;
    } else if (typeof error === 'object') {
        return error as ErrorRecord<any>;
    }
    return String(error);
}

function createTransformEntry(
    index: number,
    valid: boolean,
    result?: any,
    error?: string | ErrorRecord<any>,
    nameOfPipe?: string,
): ExecutionEntry {
    return { type: 'transform', index, valid, result, error, nameOfPipe };
}

function createValidationEntry(index: number, validation: ValidationResult, nameOfPipe?: string): ExecutionEntry {
    const error = validation.valid
        ? undefined
        : validation.error ?? 'Validation failed';
    return { type: 'validate', index, valid: validation.valid, validation, error, exitEarly: validation.exitEarly, nameOfPipe };
}

function filterResults<T>(execution: ExecutionEntry[]): T[] {
    return execution
        .filter(e => e.type === 'transform' && e.valid)
        .map(e => e.result);
}

function filterValidations(execution: ExecutionEntry[]): ValidationResult[] {
    return execution
        .filter(e => e.type === 'validate' && e.validation)
        .map(e => e.validation!);
}

function filterErrors(execution: ExecutionEntry[]): ErrorEntry[] {
    return execution
        .filter(e => !e.valid && e.error)
        .map(e => ({ index: e.index, error: e.error!, nameOfPipe: e.nameOfPipe }));
}

function hasErrors(execution: ExecutionEntry[]): boolean {
    return execution.some(e => !e.valid);
}

function hasEarlyExit(execution: ExecutionEntry[]): {errorFound: boolean, exitEarlyFound: boolean} {
    let errorFound = false;
    let exitEarlyFound = false;

    execution.forEach(e => {
        if (e.error) {
            errorFound = true;
        }
        if (e.exitEarly) {
            exitEarlyFound = true;
        }
    })

    return {errorFound, exitEarlyFound};
}

function shouldEarlyExit(options: PipeOptions, execution: ExecutionEntry[]): boolean {
    const evaluate = hasEarlyExit(execution);
    return (
        (evaluate.exitEarlyFound)
        || (options.stopOnError === true && evaluate.errorFound)
    );
}

/**
 * Wrapper for final pipe result
 */
export class PipeResult<T> {
    private _value: T;
    private _execution: ExecutionEntry[];
    private _options: PipeOptions;

    constructor(
        value: T,
        execution: ExecutionEntry[],
        options: PipeOptions,
    ) {
        this._value = value;
        this._execution = execution;
        this._options = options;
    }

    /**
   * Get the final value
   * @returns The final transformed value
   */
    getValue(): T {
        return this._value;
    }

    /**
     * 
     */
    getOptions(): PipeOptions {
        return this._options;
    }

    /**
   * Get all successful transformation results
   * @returns Array of intermediate transformation results
   */
    getResults(): T[] {
        return filterResults(this._execution);
    }

    /**
   * Get all validation results
   * @returns Array of validation results
   */
    getValidations(): ValidationResult[] {
        return filterValidations(this._execution);
    }

    /**
   * Get complete execution history
   * @returns Array of all execution entries
   */
    getExecution(): ExecutionEntry[] {
        return this._execution;
    }

    /**
   * Get all errors that occurred
   * @returns Array of error entries with index and message
   */
    getErrors(): ErrorEntry[] {
        return filterErrors(this._execution);
    }

    /**
   * Check if any errors occurred
   * @returns True if any step failed
   */
    hasErrors(): boolean {
        return hasErrors(this._execution);
    }

    /**
   * Check if all steps succeeded
   * @returns True if no errors occurred
   */
    isSuccess(): boolean {
        return !this.hasErrors();
    }

    getDebugString(): string {
        return `${this._options?.nameOfPipe ? 'Name of Pipeline: ' + this._options.nameOfPipe : ''} ; `
            + this._execution.map((entry) => `[${entry.index}]: ` + (entry.error ?? 'No Error Found')).join(' ; ');
    }
}

/**
 * Synchronous pipe that may become async
 */
export class SyncPipe<T> implements PromiseLike<PipeResult<T>> {
    private _value: T;
    private _execution: ExecutionEntry[];
    private _options: PipeOptions;

    constructor(
        value: T,
        execution: ExecutionEntry[] = [],
        options: PipeOptions = {},
    ) {
        this._value = value;
        this._execution = execution;
        this._options = options;
    }
    /**
   * Apply a transformation function
   * @param fn - Transform function receiving current value and optional args
   * @param args - Additional arguments passed to transform function
   * @returns New pipe with transformed value (async if fn returns Promise)
   */
    transform<R, Args extends any[] = any[]>(
        fn: (value: T, ...args: Args) => Promise<R>,
        ...args: Args
    ): AsyncPipe<R>;
    transform<R, Args extends any[] = any[]>(
        fn: (value: T, ...args: Args) => R,
        ...args: Args
    ): SyncPipe<R>;
    transform<R, Args extends any[] = any[]>(
        fn: (value: T, ...args: Args) => R | Promise<R>,
        ...args: Args
    ): SyncPipe<R> | AsyncPipe<R> {
        if (shouldEarlyExit(this._options, this._execution)) {
            return this as any;
        }

        const index = this._execution.length;
        try {
            const result = fn(this._value, ...args);

            if (isPromiseLike(result)) {
                const promise = result.then(
                    value => {
                        this._execution.push(createTransformEntry(index, true, value, undefined, this._options?.nameOfPipe));
                        return value;
                    },
                    error => {
                        this._execution.push(
                            createTransformEntry(index, false, undefined, getErrorMessage(error), this._options?.nameOfPipe),
                        );
                        return this._value as any;
                    },
                );
                return new AsyncPipe(promise, this._execution, this._options);
            }

            return new SyncPipe(
                result,
                [...this._execution, createTransformEntry(index, true, result, undefined, this._options?.nameOfPipe)],
                this._options,
            );
        } catch (error) {
            return new SyncPipe(
                this._value as any,
                [
                    ...this._execution,
                    createTransformEntry(index, false, undefined, getErrorMessage(error), this._options?.nameOfPipe),
                ],
                this._options,
            );
        }
    }

    /**
     * Apply a validation function without transforming the value
     * @param fn - Validation function that receives the current value and returns ValidationResult
     * @returns New pipe with the same value (errors are tracked separately). Returns async pipe if fn returns Promise
     */
    validate(fn: (value: T) => Promise<ValidationResult>): AsyncPipe<T>;
    validate(fn: (value: T) => ValidationResult): SyncPipe<T>;
    validate(
        fn: (value: T) => ValidationResult | Promise<ValidationResult>,
    ): SyncPipe<T> | AsyncPipe<T> {
        if (shouldEarlyExit(this._options, this._execution) ) {
            return this;
        }

        const index = this._execution.length;
        try {
            const validation = fn(this._value);

            if (isPromiseLike(validation)) {
                const promise = validation.then(
                    val => {
                        this._execution.push(createValidationEntry(index, val, this._options?.nameOfPipe));
                        return this._value;
                    },
                    error => {
                        this._execution.push(
                            createTransformEntry(index, false, undefined, getErrorMessage(error), this._options?.nameOfPipe),
                        );
                        return this._value;
                    },
                );
                return new AsyncPipe(promise, this._execution, this._options);
            }

            return new SyncPipe(
                this._value,
                [...this._execution, createValidationEntry(index, validation, this._options?.nameOfPipe)],
                this._options,
            );
        } catch (error) {
            return new SyncPipe(
                this._value,
                [
                    ...this._execution,
                    createTransformEntry(index, false, undefined, getErrorMessage(error), this._options?.nameOfPipe),
                ],
                this._options,
            );
        }
    }

    /**
   * Get the current value
   * @returns The current value in the pipe
   */
    getValue(): T {
        return this._value;
    }

    /**
   * Get all successful transformation results
   * @returns Array of intermediate transformation results
   */
    getResults(): T[] {
        return filterResults(this._execution);
    }

    /**
   * Get all validation results
   * @returns Array of validation results
   */
    getValidations(): ValidationResult[] {
        return filterValidations(this._execution);
    }

    /**
   * Get complete execution history
   * @returns Array of all execution entries
   */
    getExecution(): ExecutionEntry[] {
        return this._execution;
    }

    /**
   * Get all errors that occurred
   * @returns Array of error entries with index and message
   */
    getErrors(): ErrorEntry[] {
        return filterErrors(this._execution);
    }

    /**
   * Check if any errors occurred
   * @returns True if any step failed
   */
    hasErrors(): boolean {
        return hasErrors(this._execution);
    }

    /**
   * Check if all steps succeeded
   * @returns True if no errors occurred
   */
    isSuccess(): boolean {
        return !this.hasErrors();
    }

    then<R1, R2>(
        onfulfilled?: ((value: PipeResult<T>) => R1 | PromiseLike<R1>) | null,
        onrejected?: ((reason: any) => R2 | PromiseLike<R2>) | null,
    ): Promise<R1 | R2> {
        const result = new PipeResult(this._value, this._execution, this._options);
        return Promise.resolve(result).then(onfulfilled as any, onrejected as any);
    }
}

/**
 * Asynchronous pipe for chaining async operations
 */
export class AsyncPipe<T> implements PromiseLike<PipeResult<T>> {
    private _promise: Promise<T>;
    private _execution: ExecutionEntry[];
    private _options: PipeOptions;

    constructor(
        promise: Promise<T>,
        execution: ExecutionEntry[] = [],
        options: PipeOptions = {},
    ) {
        this._promise = promise;
        this._execution = execution;
        this._options = options;
    }
    /**
   * Apply a transformation function
   * @param fn - Transform function receiving current value and optional args
   * @param args - Additional arguments passed to transform function
   * @returns New async pipe with transformed value
   */
    transform<R, Args extends any[] = any[]>(
        fn: (value: T, ...args: Args) => R | Promise<R>,
        ...args: Args
    ): AsyncPipe<R> {
        const index = this._execution.length;
        const promise = this._promise.then(async value => {
            if (shouldEarlyExit(this._options, this._execution)) {
                return value as any;
            }

            try {
                const result = await fn(value, ...args);
                this._execution.push(createTransformEntry(index, true, result, undefined, this._options?.nameOfPipe));
                return result;
            } catch (error) {
                this._execution.push(
                    createTransformEntry(index, false, undefined, getErrorMessage(error), this._options?.nameOfPipe),
                );
                return value as any;
            }
        });

        return new AsyncPipe(promise, this._execution, this._options);
    }

    /**
     * Apply a validation function without transforming the value
     * @param fn - Validation function that receives the current value and returns ValidationResult
     * @returns New pipe with the same value (errors are tracked separately). Returns async pipe if fn returns Promise
     */
    validate(
        fn: (value: T) => ValidationResult | Promise<ValidationResult>,
    ): AsyncPipe<T> {
        const index = this._execution.length;
        const promise = this._promise.then(async value => {
            if (shouldEarlyExit(this._options, this._execution)) {
                return value;
            }

            try {
                const validation = await fn(value);
                this._execution.push(createValidationEntry(index, validation, this._options?.nameOfPipe));
                return value;
            } catch (error) {
                this._execution.push(
                    createTransformEntry(index, false, undefined, getErrorMessage(error), this._options?.nameOfPipe),
                );
                return value;
            }
        });

        return new AsyncPipe(promise, this._execution, this._options);
    }

    then<R1, R2>(
        onfulfilled?: ((value: PipeResult<T>) => R1 | PromiseLike<R1>) | null,
        onrejected?: ((reason: any) => R2 | PromiseLike<R2>) | null,
    ): Promise<R1 | R2> {
        return this._promise.then(value => {
            const result = new PipeResult(value, this._execution, this._options);
            return onfulfilled ? onfulfilled(result) : (result as any);
        }, onrejected);
    }
}

export type Pipe<T> = SyncPipe<T> | AsyncPipe<T>;

/**
 * Factory for creating function pipes
 * 
 * @example
 * Basic usage:
 * ```js
 * const result = await FunctionPipe.pipe(5)
 *   .transform(x => x * 2)
 *   .transform(x => x + 10);
 * 
 * console.log(result.getValue()); // 20
 * ```
 * 
 * @example
 * With validation:
 * ```js
 * const result = await FunctionPipe.pipe("hello")
 *   .validate(s => ({ valid: s.length > 0, error: "Empty string" }))
 *   .transform(s => s.toUpperCase());
 * 
 * if (result.isSuccess()) {
 *   console.log(result.getValue()); // "HELLO"
 * }
 * ```
 * 
 * @example
 * With stopOnError option:
 * ```js
 * const result = await FunctionPipe.pipe(5, { stopOnError: true })
 *   .transform(x => x / 0)
 *   .validate(x => ({ valid: isFinite(x), error: "Not finite" }))
 *   .transform(x => x * 2); // Won't execute
 * 
 * console.log(result.hasErrors()); // true
 * console.log(result.getErrors()); // [{ index: 1, error: "Not finite" }]
 * ```
 */
export class FunctionPipe {
    /**
   * Create a new pipe starting with the given value
   * @param value - Initial value to pipe
   * @param options - Optional configuration (stopOnError)
   * @returns New SyncPipe instance
   */
    static pipe<TInitialValue>(value: TInitialValue, options?: PipeOptions): SyncPipe<TInitialValue> {
        return new SyncPipe(value, [], options);
    }
}